import { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import axios, { type AxiosRequestConfig } from 'axios';

import { 
  unlockAudio, 
  createAudioElement, 
  playAudio,
  cleanupAudioElement,
  createAudioRecorder,
  createFallbackAudioBlob,
  createUserGestureAudioElement,
  type AudioRecorderState
} from '@/lib/audio-config';
import { devLog } from '@/lib/utils';
import { ErrorLog } from '@/lib/sentry';
import {
  GET_NEXT_MESSAGES_INTERVIEW,
  RESET_WEB_TRAIN_CHATS,
  PROCESS_MESSAGE,
  GET_SIGNED_UPLOAD_URL,
  PRESIGN_WEB_TRAIN_VIDEO_UPLOAD
} from './../api/graphql/mutations';
import { videoBlobFilename, type VideoRecordingResult } from '@/lib/videoRecorder';
import { useTranscriptionStream } from './useTranscriptionStream';
import { Message, FormKeyType, GetNextMessagesInput, Components, Chat, OpenInterviewStatus } from '@/types/api';

// Types
export interface Sequence extends Message {
  sound?: HTMLAudioElement;
  duration?: number;
}

export interface DeveloperMethods {
  updatePrompt: (promptId: string, user: string, system: string) => Promise<void>;
  updateHardCodedMessage: (messageId: string, texts: string[]) => Promise<void>;
  regenerateChat: () => Promise<void>;
  regenerateSequence: () => Promise<void>;
  resetToOnboarding: () => Promise<void>;
  resetToPreTrain: () => Promise<void>;
}

interface UseTrainType {
  status: string;
  isMuted: boolean;
  isAwake: boolean;
  toggleMuted: () => void;
  initTalk: (categoryId?: string) => Promise<void>;
  reply: () => Promise<void>;
  focus: any;
  startRecording: () => void;
  stopRecording: () => void;
  toggleRecording: () => void;
  currentMessage: Sequence | null;
  messageText?: string;
  /** 0..1 — how far through the currently-playing TTS audio we are. */
  playbackProgress: number;
  /** Absolute ms elapsed in the currently-playing TTS audio. Use with the
   * per-word startMs/endMs values in `currentMessage.questionAudioTimings`
   * to drive frame-accurate word reveal. */
  playbackElapsedMs: number;
  replyText: string;
  sequence: Sequence[] | null;
  bottomSheetOpened: boolean;
  bottomSheetKey: FormKeyType;
  showBottomSheet: () => void;
  closeBottomSheet: () => void;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  submitFormResponse: (reply: Partial<GetNextMessagesInput>) => Promise<void>;
  /** Submit a live-recorded video answer: the parallel audio track is
   *  transcribed on the fast path (so the interviewer responds in voice-note
   *  time) while the raw video uploads to S3 in the background for the
   *  production room. */
  submitVideoResponse: (result: VideoRecordingResult) => Promise<void>;
  chatComponents: Components | null;
  currentChat: Chat | null;
  resetChat: () => void;
  setIsAwake: (value: boolean) => void;
  setCategoryId: (categoryId: string | undefined) => void;
  categoryId: string | undefined;
  /** True when the backend is running this session as an open interview
   *  (model-paced rather than a fixed question list). Drives whether the
   *  progress UI uses questionsAsked/maxQuestions from openInterviewStatus. */
  isOpenInterview: boolean;
  openInterviewStatus: OpenInterviewStatus | null;
  /** Session total credits earned, present once the backend sends it on the
   *  final END_CHAT turn. */
  creditsEarned: number | null;
  /** Contributor's standing Proxi-credit balance, present on every turn. */
  creditsBalance: number | null;
  /** Live 0..1 mic level while recording. Returns 0 when not recording. */
  getMicLevel: () => number;
  /** True from the moment initTalk is invoked until the first message's
   *  audio is preloaded and isAwake flips. Callers use this to keep the
   *  WakeOverlay spinner up across the fetch+preload window so the user
   *  never lands on the interview screen mid-"Thinking". */
  isInitializing: boolean;
}

// Constants
const DEFAULT_BOTTOM_SHEET_KEY = 'textReply';
const DEFAULT_VOICE_ID = 'I3MZt3tdPM3rfbz2JVhj';

export const useTrain = (initialCategoryId?: string): UseTrainType => {
  // Refs
  const audioUrls = useRef<any>({});
  const audioRecorder = useRef<AudioRecorderState | null>(null);
  const currentAudioElement = useRef<HTMLAudioElement | null>(null);
  // Bumped on every fresh interview start (initTalk) so any in-flight
  // _createSequence / _playItem chain from a previous start can detect it has
  // been superseded and bail, instead of speaking over the new interview.
  const playbackGenerationRef = useRef(0);
  // The element that's *actually* producing sound right now: what playAudio
  // returned (often the shared iOS gesture element, not the per-item local
  // variable) or the pre-generated summary element. Tracked so stopPlayback
  // can silence exactly the element that's playing.
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  // Resolver for the playback promise currently being awaited. stopPlayback
  // invokes it so a superseded reply()/_createSequence chain unwinds instead
  // of hanging forever on an `onended` that will never fire once we've paused
  // the element out from under it.
  const activeAudioFinishRef = useRef<(() => void) | null>(null);
  
  // State
  const [sequence, setSequence] = useState<any>(null);
  const [isAwake, setIsAwake] = useState(false);
  const [bottomSheetOpened, setBottomSheetOpened] = useState<boolean>(false);
  const [bottomSheetKey, setBottomSheetKey] = useState<any>(DEFAULT_BOTTOM_SHEET_KEY);
  const [chatComponents, setChatComponents] = useState<any>(null);
  const [currentChat, setCurrentChat] = useState<any>(null);
  const [isOpenInterview, setIsOpenInterview] = useState<boolean>(false);
  const [openInterviewStatus, setOpenInterviewStatus] = useState<OpenInterviewStatus | null>(null);
  const [creditsEarned, setCreditsEarned] = useState<number | null>(null);
  const [creditsBalance, setCreditsBalance] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [focus, setFocus] = useState<any>(null);
  const [categoryId, setCategoryId] = useState<string | undefined>(initialCategoryId);
  const [voiceId, setVoiceId] = useState<string>(DEFAULT_VOICE_ID);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState<string>('LOADING');
  const [replyText, setReplyText] = useState('');
  const [currentMessage, setCurrentMessage] = useState<any>(null);
  const [messageText, setMessageText] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  // 0..1 progress of the currently-playing TTS audio. Consumers use this to
  // sync visual elements (e.g. word-by-word text reveal) with playback.
  const [playbackProgress, setPlaybackProgress] = useState(0);
  // Same playback signal but as an absolute ms timestamp from the start of
  // the audio. Used to drive word reveal against per-word startMs/endMs
  // values returned by ElevenLabs' with-timestamps endpoint.
  const [playbackElapsedMs, setPlaybackElapsedMs] = useState(0);
  const progressRafRef = useRef<number | null>(null);
  
  // Initialize recorder on first use
  if (!audioRecorder.current) {
    audioRecorder.current = createAudioRecorder();
  }

  // Streaming transcription via OpenAI Realtime (with batch /api/transcribe fallback)
  const transcribeStream = useTranscriptionStream();
  const liveTranscriptRef = useRef<string | null>(null);
  
  // Hooks
  const [resetWebTrainChatsMutation] = useMutation<any, any>(RESET_WEB_TRAIN_CHATS);
  const [processMessageMutation] = useMutation<any, any>(PROCESS_MESSAGE);
  const [getSignedUploadUrl] = useMutation<any, any>(GET_SIGNED_UPLOAD_URL);
  const [presignWebTrainVideoUpload] = useMutation<any, any>(PRESIGN_WEB_TRAIN_VIDEO_UPLOAD);
  const [replyMutation] = useMutation<any, any>(GET_NEXT_MESSAGES_INTERVIEW);

  // Recording Functions
  const startRecording = async () => {
    try {
      if (audioRecorder.current) {
        await audioRecorder.current.startRecording();
        setIsRecording(true);
        setRecordingBlob(null);
        liveTranscriptRef.current = null;
        // Kick off streaming transcription in parallel. If it fails, the
        // existing MediaRecorder blob → /api/transcribe path still runs.
        transcribeStream.start().catch(err => {
          devLog.warn('Streaming transcription failed to start, will fall back to batch:', err?.message || err);
        });
        devLog.log('Started recording with shared context');
      }
    } catch (error) {
      devLog.error('Failed to start recording:', error);
      ErrorLog(error);
    }
  };

  const stopRecording = () => {
    try {
      if (audioRecorder.current) {
        // Capture user gesture for audio playback
        createUserGestureAudioElement();

        // Stop the recording
        audioRecorder.current.stopRecording();
        setIsRecording(false);

        // Ask the streaming transcription for the final transcript. If it
        // arrives within the timeout, use it directly and skip the batch
        // /api/transcribe upload. The `recordingBlob` effect checks
        // liveTranscriptRef.current to decide which path to take.
        transcribeStream
          .stopAndAwaitFinal(8000)
          .then(text => {
            liveTranscriptRef.current = text || '';
            devLog.log(`[STT-stream] final transcript: "${text}"`);
          })
          .catch(err => {
            devLog.warn(`[STT-stream] no live transcript, will fall back to batch: ${err?.message || err}`);
          });
        
        // Set up a delay to get the blob with fallback for simulator
        setTimeout(async () => {
          if (audioRecorder.current?.recordingBlob) {
            const blob = audioRecorder.current.recordingBlob;
            devLog.log(`Recording blob ready: ${blob.size} bytes, type: ${blob.type}`);
            setRecordingBlob(blob);

            // Check for very small blobs (common in iOS simulator during development)
            // Only use fallback in development mode to avoid unnecessary processing in production
            if (blob.size < 100 && import.meta.env.DEV) {
              devLog.warn(`Blob too small (${blob.size} bytes), creating fallback for development/simulator`);
              try {
                const fallbackBlob = await createFallbackAudioBlob();
                devLog.log(`Using fallback blob for dev/simulator: ${fallbackBlob.size} bytes, type: ${fallbackBlob.type}`);
                setRecordingBlob(fallbackBlob);
              } catch (error) {
                devLog.error('Failed to create fallback blob:', error);
                setRecordingBlob(blob); // Use original even if small
              }
            } else {
              setRecordingBlob(blob);
            }
          } else {
            // No recording blob available (common in iOS simulator during development)
            // Only create fallback in development mode
            if (import.meta.env.DEV) {
              devLog.warn('No recording blob available, creating fallback for development/simulator');
              try {
                const fallbackBlob = await createFallbackAudioBlob();
                devLog.log(`Using fallback blob for dev/simulator: ${fallbackBlob.size} bytes, type: ${fallbackBlob.type}`);
                setRecordingBlob(fallbackBlob);
              } catch (error) {
                devLog.error('Failed to create fallback blob for simulator:', error);
              }
            } else {
              devLog.warn('No recording blob available in production - skipping fallback');
            }
          }
        }, 300);
      }
    } catch (error) {
      devLog.error('Failed to stop recording:', error);
      ErrorLog(error);
    }
  };

  // Effects
  const prevIsRecording = useRef(isRecording);
  
  useEffect(() => {
    if (prevIsRecording.current !== isRecording) {
      setStatus(isRecording ? 'USER_RECORDING' : 'ROXY_TYPING');
      
      if (isRecording) {
        devLog.log('Recording started with shared audio context');
      } else {
        devLog.log('Recording stopped - user gesture captured for audio playback');
      }
    }
    prevIsRecording.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    if (recordingBlob) {
      // If streaming STT produced a final transcript, use it directly and
      // skip waiting on the batch /api/transcribe call. We still upload the
      // blob in the background so the user reply is persisted to S3, but
      // reply() runs against the live transcript on the critical path.
      const liveText = liveTranscriptRef.current;
      if (liveText && liveText.trim() !== '') {
        liveTranscriptRef.current = null;
        void (async () => {
          try {
            setStatus('ROXY_TYPING');
            const messageBeingRepliedTo = currentMessage;
            const audioUploadPromise = uploadUserAudio(recordingBlob, messageBeingRepliedTo?._id);
            await reply({ text: liveText });
            const uploadedAudioUrl = await audioUploadPromise.catch(() => '');
            _processReply(messageBeingRepliedTo, { text: liveText }, uploadedAudioUrl);
          } catch (e) {
            setStatus('IDLE');
            ErrorLog(e);
          }
        })();
      } else {
        transcribeAndReply(recordingBlob);
      }
    }
  }, [recordingBlob]);

  useEffect(() => {
    if (isAwake && status === 'LOADING') {
      setStatus('IDLE');
    }
  }, [isAwake, status]);

  // Main Functions
  const initTalk = async (overrideCategoryId?: string) => {
    // Cleanly close any interview that's still playing before opening the new
    // one. Without this a second start (e.g. the embedded auto-wake racing the
    // global-link confirmation) leaves the previous _createSequence loop
    // running, so both interviews' questions speak at once.
    stopPlayback();
    // Capture user gesture for audio playback
    createUserGestureAudioElement();

    // Simple audio unlock - only call once
    await unlockAudio();
    // setIsAwake is deferred until the first message's audio is preloaded
    // (handled by _createSequence / the shortSessionSummary branch in reply).
    // Until then, isInitializing keeps the WakeOverlay's spinner up so the
    // user doesn't dump onto the interview screen and watch a "Thinking" gap.
    setIsInitializing(true);

    // If categoryId is provided, set it in state for future use
    if (overrideCategoryId) {
      setCategoryId(overrideCategoryId);
    }

    try {
      // Pass categoryId directly to ensure it's available for the first message
      const categoryIdToUse = overrideCategoryId || categoryId;
      const replyData = categoryIdToUse ? { categoryId: categoryIdToUse } : undefined;
      await reply(replyData);
    } catch (e) {
      ErrorLog(e);
    } finally {
      // Safety net for the edge case where neither preload path triggered
      // wake-up (empty messages + no summary). Idempotent if already cleared.
      setIsInitializing(false);
    }
  };

  const toggleMuted = () => {
    setIsMuted((isMuted) => !isMuted);
  };

  // Utility Functions
  const _uploadBlob = async (blob: Blob, messageId: string) => {
    if (blob) {
      try {
        // Detect the actual blob format and determine appropriate file extension and content type
        const blobType = blob.type;
        let fileExtension = 'webm';
        let contentType = 'audio/webm';
        if (blobType.includes('webm')) {
          fileExtension = 'webm';
          contentType = 'audio/webm';
        } else if (blobType.includes('mp4') || blobType.includes('m4a')) {
          fileExtension = 'mp4';
          contentType = 'audio/mp4';
        } else if (blobType.includes('ogg')) {
          fileExtension = 'ogg';
          contentType = 'audio/ogg';
        } else if (blobType.includes('wav')) {
          fileExtension = 'wav';
          contentType = 'audio/wav';
        } else if (blobType.includes('mpeg') || blobType.includes('mp3')) {
          fileExtension = 'mp3';
          contentType = 'audio/mpeg';
        }
        const fileName = `userAudio/${messageId}.${fileExtension}`;
        const { data } = await getSignedUploadUrl({
          variables: {
            fileName,
            contentType,
          },
        });
        if (data?.getSignedUploadUrl) {
          const res = await fetch(data.getSignedUploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': contentType,
              'x-amz-acl': 'public-read',
            },
            body: blob,
          });
          if (res.ok) {
            devLog.log(`Uploaded blob to ${fileName}`);
            return fileName;
          }
          return '';
        }
        return '';
      } catch (e) {
        ErrorLog(e);
        return '';
      }
    }
    return '';
  };

  const _processReply = async (
    message: any,
    replyData?: any,
    audioUrl: string = '',
  ) => {
    if (message?.userResponseType !== 'REPLY' || !replyData) {
      return;
    }
    try {
      const result = await processMessageMutation({
        variables: {
          messageId: message._id,
          audioUrl,
        },
      });
      devLog.log('Process message mutation successful:', result);
    } catch (e) {
      ErrorLog(e);
    }
  };

  // Pick a filename + content-type the server /api/transcribe accepts.
  const _pickRecordingFilename = (blob: Blob): string => {
    const blobType = blob.type || '';
    if (blobType.includes('webm')) return 'recording.webm';
    if (blobType.includes('mp4') || blobType.includes('m4a')) return 'recording.mp4';
    if (blobType.includes('ogg')) return 'recording.ogg';
    if (blobType.includes('wav')) return 'recording.wav';
    if (blobType.includes('mpeg') || blobType.includes('mp3')) return 'recording.mp3';
    return 'recording.webm';
  };

  /**
   * Upload the user's recording to /api/transcribe purely to persist the
   * audio to S3 and get back the audioUrl. Used by the streaming-STT path
   * where the transcript is already known. The transcription returned by the
   * endpoint is discarded.
   */
  const uploadUserAudio = async (blob: Blob, messageId?: string): Promise<string> => {
    if (!blob || blob.size === 0) return '';
    try {
      const formData = new FormData();
      formData.append('file', blob, _pickRecordingFilename(blob));
      if (messageId) formData.append('messageId', messageId);
      const endpoint = `${import.meta.env.VITE_GRAPHQL_HOST}/api/transcribe`;
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      return (response?.data?.audioUrl as string) || '';
    } catch (e) {
      devLog.warn('uploadUserAudio failed:', (e as any)?.message || e);
      return '';
    }
  };

  // Audio Processing Functions
  const transcribeAndReply = async (blob: any) => {
    try {
      setStatus('ROXY_TYPING');
      if (!blob) {
        devLog.error('No blob provided for transcription');
        setStatus('IDLE');
        return;
      }
      
      // Validate blob
      if (blob.size === 0) {
        devLog.error('Blob is empty, cannot transcribe');
        setStatus('IDLE');
        return;
      }
      
      devLog.log(`Transcribing blob: ${blob.size} bytes, type: ${blob.type}`);
      
      // Capture the message being replied to BEFORE calling reply()
      // because reply() -> _playSequence() -> _playItem() will update currentMessage
      const messageBeingRepliedTo = currentMessage;
      
      // Send the blob directly to transcription endpoint
      // Detect blob format for proper filename
      const blobType = blob.type;
      let filename = 'recording.webm';
      if (blobType.includes('webm')) {
        filename = 'recording.webm';
      } else if (blobType.includes('mp4') || blobType.includes('m4a')) {
        filename = 'recording.mp4';
      } else if (blobType.includes('ogg')) {
        filename = 'recording.ogg';
      } else if (blobType.includes('wav')) {
        filename = 'recording.wav';
      } else if (blobType.includes('mpeg') || blobType.includes('mp3')) {
        filename = 'recording.mp3';
      }
      
      const formData = new FormData();
      formData.append('file', blob, filename);
      if (messageBeingRepliedTo?._id) {
        formData.append('messageId', messageBeingRepliedTo._id);
      }

      devLog.log(`Sending transcription request with filename: ${filename}`);
      const endpoint = `${import.meta.env.VITE_GRAPHQL_HOST}/api/transcribe`;
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000,
      });
      const transcription = response?.data?.transcription;
      const uploadedAudioUrl: string = response?.data?.audioUrl || '';
      if (!transcription || transcription.trim() === '') {
        setStatus('IDLE');
        return;
      }
      const replyData = { text: transcription };

      // Audio should be unlocked from the stop button click
      devLog.log('Processing reply - audio should be ready');

      await reply(replyData);
      // Use the captured message, not currentMessage (which is now the follow-up question)
      _processReply(messageBeingRepliedTo, replyData, uploadedAudioUrl);
    } catch (e) {
      setStatus('IDLE');
      ErrorLog(e);
    }
  };

  /**
   * Live-recorded video answer, optimized so the interviewer's reply lands in
   * voice-note time regardless of the clip's size:
   *
   *  Fast path (parallel audio track captured): the tiny audio blob goes to
   *  /api/transcribe (critical path, same as voice answers) while the raw
   *  video uploads straight to S3 via presign in the background — its public
   *  URL is known as soon as the presign returns, so the reply doesn't wait
   *  for video bytes. The transcript + video URL then ride the normal
   *  reply() turn.
   *
   *  Fallback (no audio track): one POST of the whole video to
   *  /api/transcribe; the server extracts + transcribes the audio and stores
   *  the raw video, at the cost of waiting on the full upload.
   */
  const submitVideoResponse = async ({ videoBlob, audioBlob }: VideoRecordingResult) => {
    try {
      // The "Use this answer" tap is our user gesture for follow-up playback.
      createUserGestureAudioElement();
      setStatus('ROXY_TYPING');
      if (!videoBlob || videoBlob.size === 0) {
        devLog.error('No video blob provided');
        setStatus('IDLE');
        return;
      }

      const messageBeingRepliedTo = currentMessage;
      const endpoint = `${import.meta.env.VITE_GRAPHQL_HOST}/api/transcribe`;
      const videoContentType = videoBlob.type || 'video/webm';

      /**
       * Presign, then fire the raw-video PUT without awaiting it. Returns the
       * video's public URL ('' if the presign failed) — known before a single
       * video byte has uploaded, so replies never wait on video. The backend's
       * registerVideoResponse polls S3, tolerating the object landing well
       * after the reply turn.
       */
      const startBackgroundVideoUpload = async (): Promise<string> => {
        try {
          const { data } = await presignWebTrainVideoUpload({
            variables: {
              input: {
                fileName: videoBlobFilename(videoBlob),
                contentType: videoContentType,
                fileSize: videoBlob.size,
              },
            },
          });
          const presign = data?.presignWebTrainVideoUpload;
          if (!(presign?.success && presign?.uploadUrl && presign?.publicUrl)) {
            devLog.warn('Video presign rejected:', presign?.error);
            return '';
          }
          void fetch(presign.uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': videoContentType,
              'x-amz-acl': 'public-read',
            },
            body: videoBlob,
          })
            .then(res => {
              if (!res.ok) throw new Error(`video PUT failed with status ${res.status}`);
              devLog.log('Background video upload complete');
            })
            .catch(err => {
              devLog.warn('Background video upload failed:', err?.message || err);
              ErrorLog(err);
            });
          return presign.publicUrl as string;
        } catch (presignErr) {
          devLog.warn('Video presign failed:', (presignErr as any)?.message || presignErr);
          return '';
        }
      };

      if (audioBlob && audioBlob.size >= 1024) {
        devLog.log(
          `Video answer fast path: audio ${audioBlob.size} bytes, video ${videoBlob.size} bytes`,
        );

        // Critical path: transcribe the audio track (also persists it as the
        // answer audio, same as a voice reply).
        const audioForm = new FormData();
        audioForm.append('file', audioBlob, _pickRecordingFilename(audioBlob));
        if (messageBeingRepliedTo?._id) {
          audioForm.append('messageId', messageBeingRepliedTo._id);
        }
        const transcribePromise = axios.post(endpoint, audioForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });

        const videoPublicUrl = await startBackgroundVideoUpload();

        let transcription = '';
        let uploadedAudioUrl = '';
        try {
          const response = await transcribePromise;
          transcription = response?.data?.transcription || '';
          uploadedAudioUrl = response?.data?.audioUrl || '';
        } catch (transcribeErr) {
          // Still deliver the answer — the backend folds its no-transcript
          // video marker around the placeholder.
          devLog.warn(
            'Audio transcription failed, replying with placeholder:',
            (transcribeErr as any)?.message || transcribeErr,
          );
        }

        const replyData: GetNextMessagesInput = {
          text: transcription.trim() || "I've added a video as my answer.",
          ...(videoPublicUrl ? { attachment: videoPublicUrl } : {}),
        };
        await reply(replyData);
        // Use the captured message, not currentMessage (now the follow-up).
        _processReply(messageBeingRepliedTo, replyData, uploadedAudioUrl);
        return;
      }

      // Fallback: single upload of the whole clip; server extracts the audio,
      // transcribes it, and stores the raw video.
      //
      // Guard: /api/transcribe caps video at 50MB. At the 1080p capture
      // bitrate any take longer than about a minute exceeds that — rather
      // than bounce off the cap, send the video via presigned S3 (500MB cap)
      // and submit the answer without a client transcript; the backend folds
      // its no-transcript video marker so the interviewer still acknowledges
      // it, and registerVideoResponse's media-analysis pass transcribes the
      // stored take server-side.
      const FALLBACK_TRANSCRIBE_MAX_BYTES = 48 * 1024 * 1024;
      if (videoBlob.size > FALLBACK_TRANSCRIBE_MAX_BYTES) {
        devLog.warn(
          `Video too large for transcribe fallback (${videoBlob.size} bytes) — presign upload, no transcript`,
        );
        const videoPublicUrl = await startBackgroundVideoUpload();
        if (!videoPublicUrl) {
          // Nothing was stored anywhere — surface as a failed turn so the
          // contributor can retry, instead of recording a phantom video.
          devLog.error('Oversized video could not be uploaded');
          setStatus('IDLE');
          return;
        }
        const replyData: GetNextMessagesInput = {
          text: "I've added a video as my answer.",
          attachment: videoPublicUrl,
        };
        await reply(replyData);
        _processReply(messageBeingRepliedTo, replyData, '');
        return;
      }

      devLog.log(`Video answer fallback path: ${videoBlob.size} bytes, type: ${videoContentType}`);
      const formData = new FormData();
      formData.append('file', videoBlob, videoBlobFilename(videoBlob));
      if (messageBeingRepliedTo?._id) {
        formData.append('messageId', messageBeingRepliedTo._id);
      }

      // Videos are much bigger than voice notes — allow a slow mobile upload.
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      const transcription: string = response?.data?.transcription || '';
      const uploadedAudioUrl: string = response?.data?.audioUrl || '';
      const videoUrl: string = response?.data?.videoUrl || '';

      const replyData: GetNextMessagesInput = {
        // A silent clip still counts as an answer — the backend folds its
        // no-transcript video marker around the placeholder.
        text: transcription.trim() || "I've added a video as my answer.",
        ...(videoUrl ? { attachment: videoUrl } : {}),
      };

      await reply(replyData);
      _processReply(messageBeingRepliedTo, replyData, uploadedAudioUrl);
    } catch (e) {
      setStatus('IDLE');
      ErrorLog(e);
    }
  };

  const _stopProgressTracker = () => {
    if (progressRafRef.current != null) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = null;
    }
  };

  /**
   * Hard-stop any interview playback that's currently in flight. Bumps the
   * playback generation (so the superseded chain's guards trip and it returns),
   * silences whatever element is actually playing, and resolves the awaited
   * playback promise so the old reply()/_createSequence chain unwinds cleanly
   * rather than hanging on an `onended` that can no longer fire. Safe to call
   * when nothing is playing.
   */
  const stopPlayback = () => {
    playbackGenerationRef.current += 1;
    _stopProgressTracker();
    if (activeAudioRef.current) {
      try { activeAudioRef.current.pause(); } catch { /* noop */ }
      activeAudioRef.current = null;
    }
    if (currentAudioElement.current) {
      try { currentAudioElement.current.pause(); } catch { /* noop */ }
      currentAudioElement.current = null;
    }
    const finish = activeAudioFinishRef.current;
    activeAudioFinishRef.current = null;
    if (finish) {
      try { finish(); } catch { /* noop */ }
    }
  };

  // Stop any interview audio when the tab navigates away or this hook
  // unmounts, so a lingering utterance can't bleed into the next page. Deps
  // are empty on purpose: stopPlayback only touches refs, so the first-render
  // closure stays correct for the component's whole lifetime.
  useEffect(() => {
    const onPageHide = () => stopPlayback();
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      stopPlayback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Roughly how many words ElevenLabs Flash speaks per second at default
  // pace. Slightly under-estimated so the reveal stays a hair behind the
  // voice rather than racing ahead.
  const ESTIMATED_WORDS_PER_SECOND = 2.4;
  const _estimateDurationFromText = (text: string | undefined | null): number => {
    if (!text) return 1;
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    if (words === 0) return 1;
    return Math.max(1, words / ESTIMATED_WORDS_PER_SECOND);
  };

  const _startProgressTracker = (audio: HTMLAudioElement, estimatedDuration: number) => {
    _stopProgressTracker();
    // Our training audio is served through a chunked-streaming proxy so the
    // browser doesn't know `audio.duration` while it plays (it reports
    // Infinity until the stream ends). `audio.currentTime` still advances
    // accurately though, so we use it against an estimated total derived
    // from the text length. If the real duration is known (e.g. S3 replay
    // after the upload completes), we prefer that — it auto-corrects on
    // subsequent plays.
    const tick = () => {
      if (!audio || audio.ended) {
        progressRafRef.current = null;
        return;
      }
      const total =
        isFinite(audio.duration) && audio.duration > 0 ? audio.duration : estimatedDuration;
      const p = Math.min(1, Math.max(0, audio.currentTime / total));
      setPlaybackProgress(p);
      setPlaybackElapsedMs(audio.currentTime * 1000);
      progressRafRef.current = requestAnimationFrame(tick);
    };
    progressRafRef.current = requestAnimationFrame(tick);
  };

  const _playItem = async (
    item: Sequence,
    generation?: number,
    voiceIdToUse?: string,
  ): Promise<void> => {
    const myGeneration = generation ?? playbackGenerationRef.current;
    return new Promise(async (resolve, reject) => {
      // A newer interview start superseded this sequence before we got here.
      // Bail without touching the shared UI/audio state so we don't clobber
      // the new interview's question.
      if (myGeneration !== playbackGenerationRef.current) {
        resolve();
        return;
      }
      try {
        setCurrentMessage(item);
        setMessageText(item.text);
        // Reset reveal progress for the new utterance.
        setPlaybackProgress(0);
        setPlaybackElapsedMs(0);

        const audioUrl = audioUrls.current[item._id];

        if (!audioUrl) {
          devLog.warn(`No audio URL found for item ${item._id}`);
          // No audio: show the full message immediately so it doesn't sit hidden
          // behind a stuck reveal mask.
          setPlaybackProgress(1);
          setPlaybackElapsedMs(Number.MAX_SAFE_INTEGER);
          resolve();
          return;
        }

        let audioElement: HTMLAudioElement | null = null;
        let hasResolved = false;

        const cleanup = () => {
          if (hasResolved) return; // Prevent double cleanup
          hasResolved = true;
          _stopProgressTracker();
          // Ensure any unrevealed remainder snaps in on natural end / fallback.
          setPlaybackProgress(1);
          setPlaybackElapsedMs(Number.MAX_SAFE_INTEGER);

          if (audioElement) {
            cleanupAudioElement(audioElement);
            audioElement = null;
          }
          // Release the stop handles for this item — the element is done, and
          // a later stopPlayback must not re-pause/re-resolve a finished item.
          activeAudioRef.current = null;
          activeAudioFinishRef.current = null;

          // Don't clean up blob URLs during playback - might interfere with audio context
          // Just mark as completed
          devLog.log(`Audio playback completed for item ${item._id}`);
          resolve();
        };

        let triedClientTtsFallback = false;

        const tryClientTtsFallback = async () => {
          if (triedClientTtsFallback || hasResolved) return false;
          triedClientTtsFallback = true;

          if (audioElement) {
            cleanupAudioElement(audioElement);
            audioElement = null;
          }

          devLog.log(`Server audio failed for item ${item._id}, retrying via client TTS`);
          // Pass the sequence's voice explicitly: the `voiceId` state variable
          // this closure captured can still be the default (Roxy) on the very
          // first turn, because setVoiceId(responseVoiceId) from the same
          // reply() hasn't re-rendered yet — regenerating with it would speak
          // the first question in the wrong host's voice.
          const ok = await textToSpeech(item, false, voiceIdToUse);
          if (!ok || hasResolved) return false;
          const blobUrl = audioUrls.current[item._id];
          if (!blobUrl) return false;

          try {
            audioElement = createAudioElement(blobUrl);
            audioElement.onended = cleanup;
            audioElement.onerror = () => {
              if (!hasResolved) tryTextFallback();
            };
            const targetVolume = isMuted ? 0 : 1;
            const playing = await playAudio(audioElement, targetVolume);
            if (myGeneration !== playbackGenerationRef.current) {
              try { playing.pause(); } catch { /* noop */ }
              cleanup();
              return false;
            }
            activeAudioRef.current = playing;
            activeAudioFinishRef.current = cleanup;
            // Re-aim the tracker at whichever element actually plays.
            // playAudio may swap in a stored gesture element for iOS;
            // watching the wrong element keeps currentTime stuck at 0.
            _startProgressTracker(playing, _estimateDurationFromText(item.text));
            return true;
          } catch (e) {
            devLog.warn(`Client TTS fallback playback failed for item ${item._id}:`, e);
            return false;
          }
        };

        try {
          // Create audio element
          audioElement = createAudioElement(audioUrl);

          // Set up event handlers
          audioElement.onended = cleanup;
          audioElement.onerror = async (error) => {
            devLog.warn(`Audio element error for item ${item._id}:`, error);
            if (hasResolved) return;
            const recovered = await tryClientTtsFallback();
            if (!recovered && !hasResolved) {
              tryTextFallback();
            }
          };

          // Play with proper volume
          const targetVolume = isMuted ? 0 : 1;
          const playing = await playAudio(audioElement, targetVolume);
          // A newer start superseded us while play() was resolving — silence
          // the element we just started and unwind.
          if (myGeneration !== playbackGenerationRef.current) {
            try { playing.pause(); } catch { /* noop */ }
            cleanup();
            return;
          }
          // Expose the playing element + its resolver so stopPlayback can
          // silence and unwind this exact item.
          activeAudioRef.current = playing;
          activeAudioFinishRef.current = cleanup;
          // playAudio may have swapped in a different stored element for
          // iOS unlocking — track THAT one's currentTime, not the original.
          _startProgressTracker(playing, _estimateDurationFromText(item.text));

        } catch (error) {
          devLog.warn(`Primary audio playback failed for item ${item._id}, attempting client TTS fallback:`, error);
          const recovered = await tryClientTtsFallback();
          if (!recovered) tryTextFallback();
        }

        function tryTextFallback() {
          if (hasResolved) return;

          // Clean up failed audio element
          if (audioElement) {
            cleanupAudioElement(audioElement);
            audioElement = null;
          }

          // Show text content as fallback
          devLog.log(`Audio playback failed for item ${item._id}, showing text content`);
          setMessageText(item.text);

          // Auto-complete after showing text for a reasonable time
          setTimeout(() => {
            cleanup();
          }, 4000); // 4 seconds to read the text
        }
        
      } catch (error) {
        ErrorLog(error);
        reject(error);
      }
    });
  };

  const textToSpeech = async (message: any, isFirst = false, voiceIdToUse?: string) => {
    try {
      const ELEVENLABS_URL = 'https://api.elevenlabs.io/v1/text-to-speech/';
      const finalVoiceId = voiceIdToUse || voiceId;
      // Set options for the API request.
      const options: AxiosRequestConfig = {
        method: 'POST',
        url: `${ELEVENLABS_URL}${finalVoiceId}`,
        headers: {
          accept: 'audio/mpeg', // Set the expected response type to audio/mpeg.
          'content-type': 'application/json', // Set the content type to application/json.
          'xi-api-key': import.meta.env.VITE_ELEVEN_LABS_API_KEY, // Set the API key in the headers.
        },
        data: {
          text: message?.text, // Pass in the inputText as the text to be converted to speech.
          model_id: 'eleven_v3_conversational',
        },
        responseType: 'blob', // Set the responseType to arraybuffer to receive binary data as response.
      };
      // Send the API request using Axios and wait for the response.
      const response = await axios(options);
      if (response.status === 200) {
        audioUrls.current[message._id] = URL.createObjectURL(response.data);
      }
      return true;
    } catch (e) {
      ErrorLog(e);
    }
  };

  const _createSequence = async (messages: any[], voiceIdToUse?: string, generation?: number) => {
    const myGeneration = generation ?? playbackGenerationRef.current;
    const sequence = messages;
    if (messages?.length > 0) {
      try {
        const s3BaseUrl = import.meta.env.VITE_S3_BASE_URL || 'https://proxi-media.s3.us-east-1.amazonaws.com';
        const apiBaseUrl = import.meta.env.VITE_GRAPHQL_HOST || '';

        await Promise.all(
          messages.map(async (message, index) => {
            const interviewerAudio = message.questionAudioUrl;
            if (interviewerAudio) {
              let audioUrl: string;
              if (interviewerAudio.startsWith('http')) {
                audioUrl = interviewerAudio;
              } else if (interviewerAudio.startsWith('/api/')) {
                audioUrl = `${apiBaseUrl}${interviewerAudio}`;
              } else {
                audioUrl = `${s3BaseUrl}/${interviewerAudio}`;
              }
              audioUrls.current[message._id] = audioUrl;
            } else {
              await textToSpeech(message, index === 0, voiceIdToUse);
            }
          }),
        );
      } catch (error) {
        devLog.error(`Error in TTS processing:`, error);
        ErrorLog(error);
      }
    }
    
    devLog.log('Starting sequence playback');

    // First-message wake-up: preload the first audio element to canplaythrough
    // BEFORE flipping isAwake. The WakeOverlay's spinner stays up across this
    // window so the user never sees "Thinking" between Start and the first
    // word. Capped so a slow network doesn't strand them on the spinner.
    if (!isAwake && sequence?.length > 0) {
      const firstAudioUrl = audioUrls.current[sequence[0]._id];
      if (firstAudioUrl) {
        await new Promise<void>((resolve) => {
          const preloadAudio = new Audio();
          preloadAudio.preload = 'auto';
          preloadAudio.src = firstAudioUrl;
          const timeout = setTimeout(() => {
            devLog.warn('First-message audio preload timeout, proceeding anyway');
            resolve();
          }, 3000);
          preloadAudio.oncanplaythrough = () => { clearTimeout(timeout); resolve(); };
          preloadAudio.onerror = () => {
            clearTimeout(timeout);
            devLog.warn('First-message audio preload error, proceeding anyway');
            resolve();
          };
          preloadAudio.load();
        });
      }
      setIsAwake(true);
      setIsInitializing(false);
    }

    // A newer interview start superseded us during TTS prep / preload — don't
    // flip into ROXY_SPEAKING or start the loop for a stale sequence.
    if (myGeneration !== playbackGenerationRef.current) return;

    setStatus('ROXY_SPEAKING');
    setSequence(sequence);

    for (const [index, item] of sequence.entries()) {
      // Stop the moment a newer start takes over (e.g. the user loaded a new
      // global link) rather than talking over the new interview.
      if (myGeneration !== playbackGenerationRef.current) return;
      try {
        devLog.log(`Playing sequence item ${index + 1}/${sequence.length}: ${item._id}`);
        await _playItem(item, myGeneration, voiceIdToUse);
        if (myGeneration !== playbackGenerationRef.current) return;
        
        // Add a longer delay between audio items to ensure proper cleanup and audio context stability
        if (index + 1 < sequence.length) {
          devLog.log(`Waiting before next audio item...`);
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } catch (error) {
        devLog.error(`Error playing sequence item ${index + 1}:`, error);
        ErrorLog(error);
        // Continue with next item even if current one fails
      }
      
      // Handle sequence completion
      if (index + 1 >= sequence.length) {
        devLog.log('Audio sequence completed');
        
        // Clean up blob URLs only after entire sequence is complete
        // Don't do this during individual audio playback
        setTimeout(() => {
          Object.keys(audioUrls.current).forEach(messageId => {
            const audioUrl = audioUrls.current[messageId];
            if (audioUrl && audioUrl.startsWith('blob:')) {
              URL.revokeObjectURL(audioUrl);
              delete audioUrls.current[messageId];
              devLog.log(`Cleaned up audio URL for message: ${messageId} after sequence completion`);
            }
          });
        }, 2000); // Longer delay to ensure audio is completely done
        
        // On sequence finished
        setBottomSheetKey(item?.userResponseType === 'FORM' ? (item.formKey as any) : DEFAULT_BOTTOM_SHEET_KEY);
        if (item?.userResponseType === 'NONE' || item?.userResponseType === 'ERROR_LIGHT') {
          // Call getMessages again (end of session) or show end UI
          await reply();
        } else if (item?.userResponseType === 'FORM') {
          setStatus('FORM');
          showBottomSheet();
        } else {
          setStatus('IDLE');
        }
      }
    }
  };

  const reply = async (replyData?: any) => {
    // Snapshot the playback generation for this turn. initTalk bumps it (via
    // stopPlayback) on a fresh interview start; normal answer turns don't. If a
    // newer start supersedes us mid-flight, the guards below bail so we never
    // speak over the new interview.
    const myGeneration = playbackGenerationRef.current;
    setStatus('ROXY_TYPING');
    let input: any = {};
    const isFirstMessage = !currentMessage; // Track if this is the initial call
    
    if (currentMessage) {
      input.messageId = currentMessage._id;
    }
    if (categoryId) {
      input.categoryId = categoryId;
    }
    if (replyData) {
      input = { ...input, ...replyData };
    }
    
    // Log the final input for debugging
    devLog.log('getNextMessagesInterview input:', input);
    if (input.categoryId) {
      devLog.log('✓ Including categoryId:', input.categoryId);
    } else {
      devLog.warn('✗ No categoryId in request');
    }
    
    try {
      const {
        data: {
          getNextMessagesInterview: {
            messages,
            components,
            chat,
            focus,
            category,
            voiceId: responseVoiceId,
            isOpenInterview: responseIsOpenInterview,
            openInterviewStatus: responseOpenInterviewStatus,
            creditsEarned: responseCreditsEarned,
            creditsBalance: responseCreditsBalance,
          },
        },
      } = await replyMutation({
        variables: {
          input,
        },
      });
      // A newer interview start superseded us while the request was in flight —
      // drop this response instead of rendering/speaking it over the new one.
      if (myGeneration !== playbackGenerationRef.current) return;
      setReplyText('');
      setMessageText('');
      setCurrentChat(chat);
      setChatComponents(components);
      setFocus(focus);
      if (typeof responseIsOpenInterview === 'boolean') {
        setIsOpenInterview(responseIsOpenInterview);
      }
      if (responseOpenInterviewStatus) {
        setOpenInterviewStatus(responseOpenInterviewStatus as OpenInterviewStatus);
      }
      // Update voiceId if provided in response
      if (responseVoiceId) {
        setVoiceId(responseVoiceId);
      }
      if (responseCreditsEarned != null) {
        setCreditsEarned(responseCreditsEarned);
      }
      if (responseCreditsBalance != null) {
        setCreditsBalance(responseCreditsBalance);
      }

      // Check if this is the first message and we have pre-generated session summary audio
      // Only play if there are no messages from the backend (backend intro includes summary in message)
      if (isFirstMessage && category?.shortSessionSummaryAudio && category?.shortSessionSummary && (!messages || messages.length === 0)) {
        devLog.log('Playing pre-generated session summary audio (no messages from backend)');
        
        // Store the pre-generated audio URL (prepend S3 base URL if needed)
        const s3BaseUrl = import.meta.env.VITE_S3_BASE_URL || 'https://proxi-media.s3.us-east-1.amazonaws.com';
        const audioUrl = category.shortSessionSummaryAudio.startsWith('http') 
          ? category.shortSessionSummaryAudio 
          : `${s3BaseUrl}/${category.shortSessionSummaryAudio}`;
        
        try {
          // Create audio element and preload fully before showing anything
          const summaryAudio = new Audio();
          summaryAudio.preload = 'auto';
          summaryAudio.src = audioUrl;
          
          // Wait for audio to be fully ready to play
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              devLog.warn('Audio preload timeout, proceeding anyway');
              resolve();
            }, 8000); // 8 second timeout for larger files
            
            summaryAudio.oncanplaythrough = () => {
              clearTimeout(timeout);
              devLog.log('Summary audio preloaded successfully');
              resolve();
            };
            summaryAudio.onerror = (e) => {
              clearTimeout(timeout);
              devLog.warn('Summary audio preload failed:', e);
              reject(e);
            };
            summaryAudio.load();
          });
          
          // NOW show text and play audio together - audio is already loaded
          if (!isAwake) {
            setIsAwake(true);
            setIsInitializing(false);
          }
          setStatus('ROXY_SPEAKING');
          setMessageText(category.shortSessionSummary);
          // Reset reveal progress so the word-by-word animation starts from
          // the beginning of the summary text.
          setPlaybackProgress(0);
          setPlaybackElapsedMs(0);

          // Store reference for potential pause/stop
          currentAudioElement.current = summaryAudio;
          activeAudioRef.current = summaryAudio;

          // Play and wait for completion
          await new Promise<void>((resolve) => {
            const finish = () => {
              _stopProgressTracker();
              setPlaybackProgress(1);
              setPlaybackElapsedMs(Number.MAX_SAFE_INTEGER);
              activeAudioFinishRef.current = null;
              resolve();
            };
            // Let stopPlayback unwind this await if a newer start supersedes us.
            activeAudioFinishRef.current = finish;
            summaryAudio.onended = () => {
              devLog.log('Summary audio playback completed');
              finish();
            };
            summaryAudio.onerror = () => {
              devLog.warn('Summary audio playback error');
              finish();
            };

            const targetVolume = isMuted ? 0 : 1;
            summaryAudio.volume = targetVolume;
            summaryAudio.play()
              .then(() => {
                _startProgressTracker(
                  summaryAudio,
                  _estimateDurationFromText(category.shortSessionSummary),
                );
              })
              .catch((e) => {
                devLog.warn('Summary audio play() failed:', e);
                // Fallback: show text for a few seconds
                setTimeout(finish, 4000);
              });
          });

          // Cleanup
          summaryAudio.src = '';
          currentAudioElement.current = null;
          activeAudioRef.current = null;

          // A newer start superseded us during the summary — don't fall through
          // to the first question of a stale interview.
          if (myGeneration !== playbackGenerationRef.current) return;

        } catch (preloadError) {
          devLog.warn('Audio preload failed, showing text briefly:', preloadError);
          // Fallback: show full text for a few seconds (no audio progress to
          // drive the reveal animation, so snap it to fully revealed).
          if (!isAwake) {
            setIsAwake(true);
            setIsInitializing(false);
          }
          setStatus('ROXY_SPEAKING');
          setMessageText(category.shortSessionSummary);
          setPlaybackProgress(1);
          setPlaybackElapsedMs(Number.MAX_SAFE_INTEGER);
          await new Promise(resolve => setTimeout(resolve, 4000));
        }
        
        // Clear the summary state before proceeding to first question
        setCurrentMessage(null);
        setMessageText('');
      }
      
      await _createSequence(messages, responseVoiceId || voiceId, myGeneration);
    } catch (e) {
      ErrorLog(e);
      setReplyText('');
      setMessageText('');
      setStatus('IDLE');
    }
  };

  // UI Control Functions
  const showBottomSheet = () => {
    setBottomSheetOpened(true);
  };
  const closeBottomSheet = () => {
    setBottomSheetOpened(false);
  };
  const submitFormResponse = async (replyData: any) => {
    // Capture user gesture for audio playback
    createUserGestureAudioElement();
    
    closeBottomSheet();
    await reply(replyData);
  };

  const toggleRecording = () => {
    if (status === 'USER_RECORDING') {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Event Handlers
  const resetChat = async () => {
    // Reset audio state when chat is reset
    await resetWebTrainChatsMutation();
    window?.location?.reload();
  };
  const setIsAwakeHandler = (value: boolean) => {
    setIsAwake(value);
  };

  const setIsModalOpenHandler = (value: boolean) => {
    setIsModalOpen(value);
  };

  const setCategoryIdHandler = useCallback((newCategoryId: string | undefined) => {
    setCategoryId(newCategoryId);
  }, []);

  // Return Hook Interface
  return {
    status,
    toggleMuted,
    isMuted,
    reply,
    initTalk,
    isAwake,
    setIsAwake: setIsAwakeHandler,
    toggleRecording,
    startRecording,
    stopRecording,
    currentMessage,
    messageText,
    playbackProgress,
    playbackElapsedMs,
    replyText,
    sequence,
    isModalOpen,
    setIsModalOpen: setIsModalOpenHandler,
    bottomSheetOpened,
    bottomSheetKey,
    showBottomSheet,
    closeBottomSheet,
    submitFormResponse,
    submitVideoResponse,
    chatComponents,
    currentChat,
    focus,
    resetChat,
    setCategoryId: setCategoryIdHandler,
    categoryId,
    isOpenInterview,
    openInterviewStatus,
    creditsEarned,
    creditsBalance,
    getMicLevel: () => audioRecorder.current?.getMicLevel() ?? 0,
    isInitializing,
  };
};
